import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const AddUserModal = ({ isOpen, onClose, onUserAdded }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    role: '',
    teamNumber: '',
    teamRole: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const roleOptions = [
    { value: '', label: 'Select Role', disabled: true },
    { value: 'student', label: 'Student' },
    { value: 'city_official', label: 'City Official' },
    { value: 'pcf_admin', label: 'PCF Admin' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData?.role) {
      newErrors.role = 'Role is required';
    }

    // Validate team fields for students
    if (formData?.role === 'student') {
      if (formData?.teamNumber && isNaN(formData?.teamNumber)) {
        newErrors.teamNumber = 'Team number must be a valid number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Pass fullName in the userData object
      await onUserAdded?.({
        ...formData,
        fullName: formData?.fullName
      });
      handleClose();
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        submit: error?.message || 'Failed to add user'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      fullName: '',
      email: '',
      role: '',
      teamNumber: '',
      teamRole: ''
    });
    setErrors({});
    setIsSubmitting(false);
    onClose?.();
  };

  if (!isOpen) return null;

  const isStudent = formData?.role === 'student';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Add New User</h2>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-micro"
            disabled={isSubmitting}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Full Name <span className="text-destructive">*</span>
            </label>
            <Input
              type="text"
              value={formData?.fullName}
              onChange={(e) => handleChange('fullName', e?.target?.value)}
              placeholder="Enter full name"
              error={errors?.fullName}
              disabled={isSubmitting}
            />
            {errors?.fullName && (
              <p className="text-sm text-destructive mt-1">{errors?.fullName}</p>
            )}
          </div>

          {/* Email Address */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Email Address <span className="text-destructive">*</span>
            </label>
            <Input
              type="email"
              value={formData?.email}
              onChange={(e) => handleChange('email', e?.target?.value)}
              placeholder="Enter email address"
              error={errors?.email}
              disabled={isSubmitting}
            />
            {errors?.email && (
              <p className="text-sm text-destructive mt-1">{errors?.email}</p>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Role <span className="text-destructive">*</span>
            </label>
            <Select
              options={roleOptions}
              value={formData?.role}
              onChange={(value) => handleChange('role', value)}
              placeholder="Select role"
              error={errors?.role}
              disabled={isSubmitting}
            />
            {errors?.role && (
              <p className="text-sm text-destructive mt-1">{errors?.role}</p>
            )}
          </div>

          {/* Team Number - Only show for students */}
          {isStudent && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Team Number
              </label>
              <Input
                type="number"
                value={formData?.teamNumber}
                onChange={(e) => handleChange('teamNumber', e?.target?.value)}
                placeholder="Enter team number (optional)"
                error={errors?.teamNumber}
                disabled={isSubmitting}
              />
              {errors?.teamNumber && (
                <p className="text-sm text-destructive mt-1">{errors?.teamNumber}</p>
              )}
            </div>
          )}

          {/* Team Role - Only show for students */}
          {isStudent && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Team Role
              </label>
              <Input
                type="text"
                value={formData?.teamRole}
                onChange={(e) => handleChange('teamRole', e?.target?.value)}
                placeholder="Enter team role (optional)"
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* Submit Error */}
          {errors?.submit && (
            <div className="p-3 bg-destructive/10 border border-destructive rounded-md">
              <p className="text-sm text-destructive">{errors?.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? 'Adding...' : 'Add User'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;